/**
 * Material configuration schemas
 */

import { z } from "zod";
import { Color3Schema } from "./scene.schema";

/**
 * Texture configuration
 */
export const TextureConfigSchema = z.union([
  z.string(), // Simple URL string
  z.object({
    uAng: z.number().optional(),
    uOffset: z.number().optional(),
    url: z.string(),
    uScale: z.number().optional(),
    vAng: z.number().optional(),
    vOffset: z.number().optional(),
    vScale: z.number().optional(),
    wAng: z.number().optional(),
  }),
]);

/**
 * Base material configuration
 */
const BaseMaterialConfigSchema = z.object({
  alpha: z.number().min(0).max(1).optional(),
  alphaMode: z.number().optional(),
  backFaceCulling: z.boolean().optional(),
  disableDepthWrite: z.boolean().optional(),
  disableLighting: z.boolean().optional(),
  forceDepthWrite: z.boolean().optional(),
  name: z.string(),
  needDepthPrePass: z.boolean().optional(),
  sideOrientation: z.number().optional(),
  wireframe: z.boolean().optional(),
});

/**
 * Standard material configuration
 */
export const StandardMaterialConfigSchema = BaseMaterialConfigSchema.extend({
  ambientColor: Color3Schema.optional(),
  ambientTexture: TextureConfigSchema.optional(),
  bumpTexture: TextureConfigSchema.optional(),
  diffuseColor: Color3Schema.optional(),
  diffuseTexture: TextureConfigSchema.optional(),
  disableLighting: z.boolean().optional(),
  emissiveColor: Color3Schema.optional(),
  emissiveTexture: TextureConfigSchema.optional(),
  invertRefractionY: z.boolean().optional(),
  linkEmissiveWithDiffuse: z.boolean().optional(),
  opacityTexture: TextureConfigSchema.optional(),
  reflectionTexture: TextureConfigSchema.optional(),
  refractionTexture: TextureConfigSchema.optional(),
  roughness: z.number().min(0).max(1).optional(),
  specularColor: Color3Schema.optional(),
  specularPower: z.number().min(0).optional(),
  specularTexture: TextureConfigSchema.optional(),
  type: z.literal("standard"),
  useAlphaFromDiffuseTexture: z.boolean().optional(),
  useEmissiveAsIllumination: z.boolean().optional(),
  useReflectionOverAlpha: z.boolean().optional(),
  useSpecularOverAlpha: z.boolean().optional(),
});

/**
 * PBR material configuration
 */
export const PBRMaterialConfigSchema = BaseMaterialConfigSchema.extend({
  albedoColor: Color3Schema.optional(),
  albedoTexture: TextureConfigSchema.optional(),
  ambientColor: Color3Schema.optional(),
  ambientTexture: TextureConfigSchema.optional(),
  ambientTextureImpactOnAnalyticalLights: z.number().optional(),
  ambientTextureStrength: z.number().min(0).max(1).optional(),
  bumpTexture: TextureConfigSchema.optional(),
  disableLighting: z.boolean().optional(),
  emissiveColor: Color3Schema.optional(),
  emissiveTexture: TextureConfigSchema.optional(),
  environmentIntensity: z.number().min(0).optional(),
  forceIrradianceInFragment: z.boolean().optional(),
  lightmapTexture: TextureConfigSchema.optional(),
  maxSimultaneousLights: z.number().min(0).optional(),
  metallic: z.number().min(0).max(1).optional(),
  metallicF0Factor: z.number().optional(),
  metallicReflectanceColor: Color3Schema.optional(),
  metallicTexture: TextureConfigSchema.optional(),
  microSurfaceTexture: TextureConfigSchema.optional(),
  opacityTexture: TextureConfigSchema.optional(),
  realTimeFiltering: z.boolean().optional(),
  realTimeFilteringQuality: z.number().optional(),
  reflectanceTexture: TextureConfigSchema.optional(),
  reflectionColor: Color3Schema.optional(),
  reflectionTexture: TextureConfigSchema.optional(),
  reflectivityColor: Color3Schema.optional(),
  reflectivityTexture: TextureConfigSchema.optional(),
  refractionTexture: TextureConfigSchema.optional(),
  roughness: z.number().min(0).max(1).optional(),
  type: z.literal("pbr"),
  useAlphaFresnel: z.boolean().optional(),
  useAlphaFromAlbedoTexture: z.boolean().optional(),
  useAmbientInGrayScale: z.boolean().optional(),
  useAmbientOcclusionFromMetallicTextureRed: z.boolean().optional(),
  useGLTFLightFalloff: z.boolean().optional(),
  useHorizonOcclusion: z.boolean().optional(),
  useLightmapAsShadowmap: z.boolean().optional(),
  useLinearAlphaFresnel: z.boolean().optional(),
  useMetallnessFromMetallicTextureBlue: z.boolean().optional(),
  useObjectSpaceNormalMap: z.boolean().optional(),
  usePhysicalLightFalloff: z.boolean().optional(),
  useRadianceOcclusion: z.boolean().optional(),
  useRoughnessFromMetallicTextureAlpha: z.boolean().optional(),
  useRoughnessFromMetallicTextureGreen: z.boolean().optional(),
});

/**
 * Material configuration discriminated union
 */
export const MaterialConfigSchema = z.discriminatedUnion("type", [
  StandardMaterialConfigSchema,
  PBRMaterialConfigSchema,
]);

/**
 * Inferred types
 */
export type TextureConfig = z.infer<typeof TextureConfigSchema>;
export type StandardMaterialConfig = z.infer<
  typeof StandardMaterialConfigSchema
>;
export type PBRMaterialConfig = z.infer<typeof PBRMaterialConfigSchema>;
export type MaterialConfig = z.infer<typeof MaterialConfigSchema>;

/**
 * Type guards
 */
export function isStandardMaterialConfig(
  config: MaterialConfig,
): config is StandardMaterialConfig {
  return config.type === "standard";
}

export function isPBRMaterialConfig(
  config: MaterialConfig,
): config is PBRMaterialConfig {
  return config.type === "pbr";
}

/**
 * Default configurations (separate from schemas as per CLAUDE.md)
 */
export const defaultStandardMaterialConfig: StandardMaterialConfig = {
  alpha: 1,
  ambientColor: { b: 0, g: 0, r: 0 },
  backFaceCulling: true,
  diffuseColor: { b: 0.8, g: 0.8, r: 0.8 },
  disableLighting: false,
  emissiveColor: { b: 0, g: 0, r: 0 },
  name: "StandardMaterial",
  specularColor: { b: 1, g: 1, r: 1 },
  specularPower: 64,
  type: "standard",
  wireframe: false,
};

export const defaultPBRMaterialConfig: PBRMaterialConfig = {
  albedoColor: { b: 0.8, g: 0.8, r: 0.8 },
  alpha: 1,
  backFaceCulling: true,
  disableLighting: false,
  environmentIntensity: 1,
  metallic: 0,
  name: "PBRMaterial",
  roughness: 0.5,
  type: "pbr",
  useAmbientOcclusionFromMetallicTextureRed: false,
  useMetallnessFromMetallicTextureBlue: true,
  useRoughnessFromMetallicTextureAlpha: true,
  useRoughnessFromMetallicTextureGreen: false,
  wireframe: false,
};

/**
 * Validation helpers
 */
export function validateMaterialConfig(input: unknown): MaterialConfig {
  return MaterialConfigSchema.parse(input);
}

export function safeValidateMaterialConfig(
  input: unknown,
):
  | { success: true; data: MaterialConfig }
  | { success: false; error: z.ZodError } {
  const result = MaterialConfigSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}
